<template>
    <b-overlay
        variant="white"
        :show="loading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <div>
            <b-row class="mb-2">
                <b-col>
                    <h3 class="mb-0">
                        {{ $t('Component(ReviewProcess):processes-card-label') }}
                    </h3>
                </b-col>
                <b-col class="text-right">
                    <template v-if="showCompleteActions">
                        <CommonButton
                            variant="danger"
                            class="mr-1"
                            :disabled="completeSubmitting || !allItemsReviewed"
                            @click="openCompleteModal('reject')"
                        >
                            {{ $t('Component(Button):reject') }}
                        </CommonButton>
                        <CommonButton
                            variant="primary"
                            class="mr-1"
                            :disabled="completeSubmitting || !allItemsReviewed"
                            @click="openCompleteModal('continue')"
                        >
                            {{ $t('Component(Button):complete') }}
                        </CommonButton>
                    </template>
                    <CommonButton
                        :disabled="completeSubmitting"
                        @click="goBack"
                    >
                        {{ $t('Component(Button):back') }}
                    </CommonButton>
                </b-col>
            </b-row>

            <b-row
                v-if="process"
                class="mt-2"
            >
                <b-col md="4">
                    <GeneralInformation :process="process" />
                </b-col>
                <b-col md="8">
                    <ReviewableRenderer
                        :process="process"
                        :review-capabilities="reviewCapabilities"
                        @item-saved="onItemSaved"
                    />
                </b-col>
            </b-row>

            <b-modal
                v-model="showCompleteModal"
                :title="$t('Component(ReviewProcess):reviewable-modal-label')"
                :ok-disabled="completeSubmitting"
                :cancel-disabled="completeSubmitting"
                centered
                @ok="submitComplete"
            >
                <b-alert
                    v-if="completeError"
                    variant="danger"
                    show
                >
                    {{ completeError }}
                </b-alert>

                <b-form-group
                    :label="$t('Component(Label):comment')"
                    class="mb-0"
                >
                    <b-form-textarea
                        v-model="completeComment"
                        rows="4"
                    />
                </b-form-group>
            </b-modal>
        </div>
    </b-overlay>
</template>

<script>
import {
    BOverlay,
    BRow,
    BCol,
    BModal,
    BFormGroup,
    BFormTextarea,
    BAlert,
} from 'bootstrap-vue'
import { Button as CommonButton } from '@core/components/common'
import { ResearchReviewApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import GeneralInformation from './components/GeneralInformation.vue'
import ReviewableRenderer from './components/ReviewableRenderer.vue'

export default {
    name: 'ResearchReviewCard',
    components: {
        BOverlay,
        BRow,
        BCol,
        BModal,
        BFormGroup,
        BFormTextarea,
        BAlert,
        CommonButton,
        GeneralInformation,
        ReviewableRenderer,
    },
    mixins: [backendResponse],
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            process: null,
            showCompleteModal: false,
            completeDecision: 'continue',
            completeComment: '',
            completeError: '',
            completeSubmitting: false,
        }
    },
    computed: {
        reviewCapabilities() {
            return {
                canEditExtractedProducts: true,
                canEditExtractedSubstances: true,
                canEditProductLinks: true,
                canEditSubstanceLinks: true,
            }
        },
        showCompleteActions() {
            return this.process && this.process.status !== 'completed'
        },
        allItemsReviewed() {
            const items = this.process && Array.isArray(this.process.items) ? this.process.items : []
            return items.length > 0 && items.every(item => item && item.status === 'reviewed')
        },
    },
    created() {
        this.loadProcess()
    },
    methods: {
        goBack() {
            this.$router.push({ name: 'research-review-grid' })
        },
        openCompleteModal(decision) {
            this.completeDecision = decision
            this.completeComment = ''
            this.completeError = ''
            this.showCompleteModal = true
        },
        onItemSaved(savedItem) {
            if (!this.process || !savedItem || !savedItem.id) {
                return
            }

            const items = Array.isArray(this.process.items) ? this.process.items : []
            const index = items.findIndex(item => item && item.id === savedItem.id)
            if (index === -1) {
                return
            }

            this.$set(this.process.items, index, savedItem)
            this.process.itemsReviewed = this.process.items.filter(item => item && item.status === 'reviewed').length
        },
        submitComplete(event) {
            if (!this.process || !this.process.processId) {
                event.preventDefault()
                return
            }

            if (this.completeSubmitting) {
                event.preventDefault()
                return
            }

            event.preventDefault()
            this.completeError = ''
            this.completeSubmitting = true

            ResearchReviewApi
                .completeProcess(this.process.processId, {
                    decision: this.completeDecision,
                    comment: this.completeComment || undefined,
                })
                .then(() => {
                    this.showCompleteModal = false
                    return this.loadProcess()
                })
                .catch(e => {
                    if (e && e.response && e.response.status === 409) {
                        this.completeError = e.response.data && e.response.data.message
                            ? e.response.data.message
                            : this.$t('Component(ReviewProcess):complete-not-allowed')
                        return
                    }

                    this.handleBackendError(e)
                })
                .finally(() => {
                    this.completeSubmitting = false
                })
        },
        loadProcess() {
            this.loading = true

            return ResearchReviewApi
                .getProcess(this.id)
                .then(response => {
                    this.process = response.data
                })
                .catch(e => {
                    this.handleBackendError(e)
                })
                .finally(() => {
                    this.loading = false
                })
        },
    },
}
</script>
